import { createSelector } from 'reselect';
import type { Option } from '@branchjs/neo-components';
import { isFeatureEnabled } from '../../redux/domains/FlipperUpdates/Flipper.selectors';
import { selectIsOttActive as selectIsOttFeatureSubscribed } from './SubscriptionsSelectors';

import type { ShareableData } from '../../common/types/shareableTypes';
import { OTT_ANALYTICS_NAMESPACE } from '../../../../shared/constants/shareableReports';
import { handleGetDateRangeInfo } from '../support/dateUtils';
import { regenerateSelectedColumns } from '../../common/support/utils';
import { ANALYTICS_CONTEXT, DAY_GRANULARITY } from '../../../../shared/constants/analytics';
import type { NewDateRangeInfo } from '../types/dateRangeTypes';
import { OTT_ANALYTICS_COLUMNS } from '../screens/ScreenOtt/ott.constants';
import { OTT, OTT_CUSTOM_PARTNERS } from '../../../../shared/constants/flipperFlags';
import { METRIC_DATA_FIELD_KEYS } from '../../../../shared/constants';
import { createCustomCalculationColumnsSelector } from './CustomCalculationSelectors';
import { handleGetNewDateRangeInfo } from '../support/Dates/dateUtils';
import { getCurrentAppLocalCurrency } from './AppSelectors';
import {
  createCustomEventColumnsSelector,
  createDatasourceColumnsSelector,
  createDimensionColumnsSelector,
} from './Analytics';
import type { RootState } from 'client/js/redux';
import { isRevenueDataRestricted } from 'client/js/main/selectors/AccessControlSelectors';

const getColumnOptions = createSelector(getCurrentAppLocalCurrency, (localCurrency?: string) => {
  return { localCurrency };
});

const getDatasources = () => Object.values(OTT_ANALYTICS_COLUMNS);

const getShareableState = (state): ShareableData => state.shareable.data;

export const selectOttShareableState = createSelector(
  getShareableState,
  (shareableState) => shareableState[OTT_ANALYTICS_NAMESPACE] || {}
);

const selectReportTableState = createSelector(
  selectOttShareableState,
  (ottShareableState) => ottShareableState[ANALYTICS_CONTEXT.OTT_TABLE] || {}
);

const selectReportQuery = createSelector(selectReportTableState, (reportTableState) => {
  return reportTableState.query || {};
});

const getReportDimensions = createSelector(selectReportQuery, (reportQuery) => {
  return reportQuery.dimensions || [];
});

export const selectDateRangeInfo = createSelector(selectOttShareableState, (ottShareableState) => {
  const { dateRange, defaultRange } = ottShareableState;
  const dateRangeInfo = {
    dateRange,
    defaultRange,
  };

  return handleGetDateRangeInfo(dateRangeInfo);
});

export const selectNewDateRangeInfo = createSelector(selectOttShareableState, (ottShareableState) => {
  const { dateRange, defaultRange } = ottShareableState as NewDateRangeInfo;
  return handleGetNewDateRangeInfo({
    dateRange,
    defaultRange,
  });
});

export const selectReportGranularity = createSelector(selectReportTableState, (reportTableState) => {
  return reportTableState.granularity || 'all';
});

export const selectReportMetric = createSelector(selectReportTableState, (reportTableState) => {
  return reportTableState.unique ? 'unique_count' : 'total_count';
});

export const selectDimensionColumns = createDimensionColumnsSelector(getReportDimensions);
const selectDatasourceColumns = createDatasourceColumnsSelector(getDatasources, getColumnOptions);
const selectCustomEventColumns = createCustomEventColumnsSelector();
const selectCustomCalculationEventColumns = createCustomCalculationColumnsSelector(getColumnOptions);

export const selectAllColumns = createSelector(
  selectDimensionColumns,
  selectDatasourceColumns,
  selectCustomEventColumns,
  selectCustomCalculationEventColumns,
  (dimensionColumns, datasourceColumns, customEventColumns, customCalculationColumns) => {
    return [...dimensionColumns, ...datasourceColumns, ...customEventColumns, ...customCalculationColumns];
  }
);

export const selectSelectedColumns = createSelector(
  selectOttShareableState,
  selectAllColumns,
  (ottShareableState, allColumns) => {
    return regenerateSelectedColumns(ottShareableState.selectedColumns, allColumns);
  }
);

export const selectAllDataSourcesForWidgets = createSelector(
  selectDatasourceColumns,
  selectCustomEventColumns,
  isRevenueDataRestricted,
  (datasourceColumns, customEventColumns, revenueDataRestricted) => {
    const allDataSources = [...datasourceColumns, ...customEventColumns].map(
      (column) =>
        ({
          label: column.display,
          value: column.key,
        }) as Option
    );

    return revenueDataRestricted
      ? allDataSources.filter(
          (datasource) =>
            datasource.value !== METRIC_DATA_FIELD_KEYS.REVENUE &&
            datasource.value !== METRIC_DATA_FIELD_KEYS.REVENUE_IN_LOCAL_CURRENCY
        )
      : allDataSources;
  }
);

const selectTrendsQuery = createSelector(selectOttShareableState, (seoShareableState) => {
  return seoShareableState[ANALYTICS_CONTEXT.OTT_TRENDS];
});

export const selectTrendsGranularity = createSelector(selectTrendsQuery, (trendsQuery = {}) => {
  return trendsQuery.granularity || DAY_GRANULARITY;
});

export const selectIsOttFlipperEnabled = createSelector(
  (state: RootState) => isFeatureEnabled(state, OTT),
  (isOttFlipperEnabled) => isOttFlipperEnabled
);

export const selectIsOttEnabled = createSelector(
  [selectIsOttFlipperEnabled, selectIsOttFeatureSubscribed],
  (isOttFlipperEnabled: boolean, isOttFeatureSubscriptionActive: boolean) => {
    return isOttFlipperEnabled && isOttFeatureSubscriptionActive;
  }
);

export const selectIsOttCustomPartnersEnabled = createSelector(
  (state: RootState) => isFeatureEnabled(state, OTT_CUSTOM_PARTNERS),
  (isOttCustomPartnersFlipperEnabled) => isOttCustomPartnersFlipperEnabled
);

const selectWidgetsState = createSelector(
  selectOttShareableState,
  (ottShareableState) => ottShareableState[ANALYTICS_CONTEXT.OTT_WIDGETS] || {}
);

const selectWidgetsQuery = createSelector(selectWidgetsState, (widgetsState) => {
  return widgetsState.query || {};
});

export const selectWidgetsFilter = createSelector(selectWidgetsQuery, (widgetsQuery) => {
  return widgetsQuery.filter || {};
});

export const selectWidgetsConfig = createSelector(selectWidgetsState, (widgetsState) => {
  return widgetsState.widgets || [];
});

export const selectWidgetsOrganic = createSelector(selectWidgetsState, (widgetsState) => {
  return widgetsState.organic;
});

export const selectWidgetsUnique = createSelector(selectWidgetsState, (widgetsState) => {
  return widgetsState.unique;
});
